/**
 * The corrected answer as fields: each field a label over its value, edited
 * the way it reads (`value-editors.tsx`), × to drop it, and "Add field" for
 * one the agent left out. Nothing else.
 */

import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ValueEditor } from "./value-editors";

function AddField({ taken, onAdd }: { taken: string[]; onAdd: (name: string) => void }) {
  const [name, setName] = useState<string | null>(null);
  if (name === null) {
    return (
      <Button size="sm" onClick={() => setName("")}>
        <Plus size={13} strokeWidth={2.2} aria-hidden />
        Add field
      </Button>
    );
  }
  const key = name.trim();
  const add = () => key && !taken.includes(key) && (onAdd(key), setName(null));
  return (
    <input
      autoFocus
      className="inp min-w-0 flex-1 font-mono text-xs"
      aria-label="New field name"
      placeholder="Field name, then Enter"
      value={name}
      onChange={(e) => setName(e.target.value)}
      onBlur={() => !key && setName(null)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.preventDefault(), add());
        if (e.key === "Escape") setName(null);
      }}
    />
  );
}

export function FieldsEditor({
  value,
  original,
  onChange,
}: {
  value: Record<string, unknown>;
  original: Record<string, unknown>;
  onChange: (v: unknown) => void;
}) {
  return (
    <>
      {Object.entries(value).map(([k, v]) => (
        <div key={k} className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            <label htmlFor={`corrected-${k}`} className="font-mono text-xs text-muted">
              {k}
            </label>
            <button
              type="button"
              aria-label={`Remove field ${k}`}
              title="Remove this field"
              className="rounded p-0.5 text-faint hover:bg-hover hover:text-ink"
              onClick={() => onChange(Object.fromEntries(Object.entries(value).filter(([key]) => key !== k)))}
            >
              <X size={13} strokeWidth={2.2} />
            </button>
          </div>
          <ValueEditor
            id={`corrected-${k}`}
            value={v}
            original={original[k]}
            onChange={(next) => onChange({ ...value, [k]: next })}
          />
        </div>
      ))}
      <div className="flex items-center gap-3">
        <AddField taken={Object.keys(value)} onAdd={(k) => onChange({ ...value, [k]: "" })} />
      </div>
    </>
  );
}
